import React, { useState, useEffect } from 'react';
import { FloorRow } from './FloorRow';
import { ApartmentDialog } from './ApartmentDialog';
import { Settings, Download, ArrowLeft, Building2, User, Briefcase, Database, Archive, FileUp, Camera, Loader, QrCode, Trash2 } from 'lucide-react';
import { generatePDF } from '../utils/pdf';
import { useLocalAuth } from '../hooks/useLocalAuth';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalBuilding } from '../hooks/useLocalBuilding';
import { getBuildingCompletionPercentage, deleteBuilding } from '../lib/localDatabase';
import { BuildingEditor } from './BuildingEditor';
import { PDFImportDialog } from './PDFImportDialog';
import { PhotoUploader } from './PhotoUploader';
import { ApartmentQuickScan } from './ApartmentQuickScan';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { isOfflineModeEnabled, setOfflineMode } from '../lib/offlineStorage';

export const BuildingView: React.FC = () => {
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);
  const { user } = useLocalAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { building, loadBuilding, updateBuildingInfo, updateApartment } = useLocalBuilding();
  const [showEditor, setShowEditor] = useState(false);
  const [showArchiveButton, setShowArchiveButton] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showQuickScan, setShowQuickScan] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewBuilding, setIsNewBuilding] = useState(false);

  useEffect(() => {
    // If an ID is present, load the building from local storage
    if (id) {
      if (id === 'new') {
        setIsNewBuilding(true);
        // Automatically show the editor for new buildings
        setShowEditor(true);
      } else {
        try {
          loadBuilding(id);
          setIsNewBuilding(false);
        } catch (error) {
          console.error('Error loading building:', error);
          setError(error instanceof Error ? error.message : 'Error loading building');
          
          // If there's an error loading the building, switch to offline mode
          setOfflineMode(true);
        }
      }
    }
  }, [id, loadBuilding]);

  useEffect(() => {
    // Check if building is complete to show archive button
    if (building && building.floors.length > 0) {
      const completionPercentage = getBuildingCompletionPercentage(building);
      setShowArchiveButton(completionPercentage === 100);
    } else {
      setShowArchiveButton(false);
    }
  }, [building]);

  const handleApartmentClick = (apartment: Apartment) => {
    // Add buildingId to apartment before setting it
    setSelectedApartment({
      ...apartment,
      buildingId: building.id
    });
  };

  const handleDeleteFloor = (floorId: string) => {
    if (window.confirm('Möchten Sie diese Etage wirklich löschen?')) {
      const newBuilding = {
        ...building,
        floors: building.floors.filter(floor => floor.id !== floorId),
      };
      updateBuildingInfo(newBuilding);
    }
  };

  const handleDeleteApartment = (floorId: string, apartmentId: string) => {
    if (window.confirm('Möchten Sie diese Wohnung wirklich löschen?')) {
      const newBuilding = {
        ...building,
        floors: building.floors.map(floor => {
          if (floor.id !== floorId) return floor;
          return {
            ...floor,
            apartments: floor.apartments.filter(apt => apt.id !== apartmentId),
          };
        }),
      };
      updateBuildingInfo(newBuilding);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      await generatePDF(building);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Fehler beim Erstellen der PDF-Datei');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSaveBuilding = (updatedBuilding: Building) => {
    try {
      console.log("Saving building:", updatedBuilding);
      updateBuildingInfo(updatedBuilding)
        .then((result) => {
          setShowEditor(false);
          setError(null);
          
          // If this is a new building, navigate to the building page with the new ID
          if (isNewBuilding && result && result.id) {
            navigate(`/building/${result.id}`, { replace: true });
            setIsNewBuilding(false);
          }
        })
        .catch(error => {
          console.error('Error saving building:', error);
          setError(error instanceof Error ? error.message : 'Error saving building');
          
          // If there's an error saving the building, switch to offline mode
          setOfflineMode(true);
          
          // Try again in offline mode
          updateBuildingInfo(updatedBuilding)
            .then((result) => {
              setShowEditor(false);
              setError(null);
              
              // If this is a new building, navigate to the building page with the new ID
              if (isNewBuilding && result && result.id) {
                navigate(`/building/${result.id}`, { replace: true });
                setIsNewBuilding(false);
              }
            })
            .catch(secondError => {
              console.error('Error saving building in offline mode:', secondError);
              setError(secondError instanceof Error ? secondError.message : 'Error saving building');
            });
        });
    } catch (error) {
      console.error('Error saving building:', error);
      setError(error instanceof Error ? error.message : 'Error saving building');
    }
  };

  const handleArchiveBuilding = () => {
    if (window.confirm('Möchten Sie dieses Gebäude archivieren? Es wird in das Archiv verschoben und aus der aktiven Liste entfernt.')) {
      navigate('/dashboard');
      // The actual archiving happens in the dashboard component
    }
  };

  const handleDeleteBuilding = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteBuilding = () => {
    if (building.id) {
      deleteBuilding(building.id);
      navigate('/dashboard');
    }
    setShowDeleteConfirm(false);
  };

  const handleImportBuilding = (importedBuilding: Building) => {
    // Merge the imported building with the current one
    // Keep the current building's ID and metadata
    const mergedBuilding = {
      ...building,
      name: importedBuilding.name,
      address: importedBuilding.address,
      floors: importedBuilding.floors
    };
    
    updateBuildingInfo(mergedBuilding);
    setShowImportDialog(false);
  };

  const handleQuickScanResult = (apartment: Apartment, floorId: string) => {
    setShowQuickScan(false);
    
    // Add buildingId to apartment before setting it
    setSelectedApartment({
      ...apartment,
      buildingId: building.id
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {error && (
        <div className="mb-4 bg-red-50 text-red-700 p-4 rounded-lg">
          <p>{error}</p>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex items-start space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors shrink-0"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div className="p-3 bg-blue-600 rounded-lg shrink-0">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 break-words">
                {building.name || 'Neues Gebäude'}
              </h1>
              <p className="text-gray-600 mt-1 break-words">
                {building.address || 'Keine Adresse angegeben'}
              </p>
              {user && (
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1 shrink-0" />
                    <span className="break-words">{user.name}</span>
                  </div>
                  {user.company && (
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 mr-1 shrink-0" />
                      <span className="break-words">{user.company}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowQuickScan(true)}
              className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 transition-all duration-200"
              title="Wohneinheit scannen"
            >
              <QrCode className="w-5 h-5" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Wohneinheit scannen
              </span>
            </button>
            
            <button
              onClick={() => setShowPhotoUploader(true)}
              className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-500 text-white hover:bg-green-600 transition-all duration-200"
              title="Fotos hinzufügen"
            >
              <Camera className="w-5 h-5" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Fotos hinzufügen
              </span>
            </button>
            
            <button
              onClick={() => setShowImportDialog(true)}
              className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-all duration-200"
              title="PDF importieren"
            >
              <FileUp className="w-5 h-5" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                PDF importieren
              </span>
            </button>
            
            {showArchiveButton && (
              <button
                onClick={handleArchiveBuilding}
                className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-all duration-200"
                title="Gebäude archivieren"
              >
                <Archive className="w-5 h-5" />
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Archivieren
                </span>
              </button>
            )}
            <button
              onClick={handleExportPDF}
              disabled={isGeneratingPDF}
              className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-all duration-200 disabled:bg-emerald-300 disabled:cursor-not-allowed"
              title="PDF Export"
            >
              {isGeneratingPDF ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                PDF Export
              </span>
            </button>
            <button
              onClick={() => setShowEditor(true)}
              className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-all duration-200"
              title="Struktur bearbeiten"
            >
              <Settings className="w-5 h-5" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Struktur bearbeiten
              </span>
            </button>
            <button
              onClick={handleDeleteBuilding}
              className="group relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-500 text-white hover:bg-red-600 transition-all duration-200"
              title="Gebäude löschen"
            >
              <Trash2 className="w-5 h-5" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Löschen
              </span>
            </button>
          </div>
        </div>
        
        <div className="mt-4 rounded-md bg-blue-50 p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <Database className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Lokaler Modus aktiv:</strong> Alle Änderungen werden im Browser gespeichert.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floors */}
      <div className="space-y-4">
        {building.floors.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Keine Etagen vorhanden
            </h3>
            <p className="text-gray-500 mb-4">
              Fügen Sie Etagen hinzu, um mit der Installation zu beginnen.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => setShowImportDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <FileUp className="w-4 h-4" />
                <span>PDF importieren</span>
              </button>
              
              <button
                onClick={() => setShowEditor(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Gebäudestruktur bearbeiten</span>
              </button>
            </div>
          </div>
        ) : (
          building.floors
            .sort((a, b) => b.number - a.number)
            .map(floor => (
              <FloorRow
                key={floor.id}
                floor={floor}
                onApartmentClick={handleApartmentClick}
                onDeleteFloor={handleDeleteFloor}
                onDeleteApartment={handleDeleteApartment}
              />
            ))
        )}
      </div>

      {selectedApartment && (
        <ApartmentDialog
          apartment={selectedApartment}
          onClose={() => setSelectedApartment(null)}
          onSave={(updatedApartment) => {
            const floorId = building.floors.find(floor => 
              floor.apartments.some(apt => apt.id === updatedApartment.id)
            )?.id;
            
            if (floorId) {
              updateApartment(floorId, updatedApartment.id, updatedApartment);
            }
            
            setSelectedApartment(null);
          }}
        />
      )}

      {showEditor && (
        <BuildingEditor
          building={building}
          onSave={handleSaveBuilding}
          onClose={() => setShowEditor(false)}
        />
      )}
      
      {showImportDialog && (
        <PDFImportDialog
          onClose={() => setShowImportDialog(false)}
          onImport={handleImportBuilding}
        />
      )}

      {showPhotoUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <PhotoUploader
              buildingId={building.id}
              apartmentId=""
              onUploadComplete={() => setShowPhotoUploader(false)}
              onCancel={() => setShowPhotoUploader(false)}
            />
          </div>
        </div>
      )}

      {showQuickScan && (
        <ApartmentQuickScan
          building={building}
          onClose={() => setShowQuickScan(false)}
          onApartmentFound={handleQuickScanResult}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmDialog
          onConfirm={confirmDeleteBuilding}
          onCancel={() => setShowDeleteConfirm(false)}
          isArchived={false}
          title="Gebäude löschen"
          message="Möchten Sie dieses Gebäude wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
        />
      )}
    </div>
  );
};